import 'vendor/jquery.isOnScreen';

import VideoPreview from 'components/videoPreview/videoPreview';
import { checkAutoplayCapabilities } from 'components/videoPreview/videoPreviewCapabilities';
import { VideoPreviewEvent, LocalStorageName } from 'components/videoPreview/videoPreviewConstants';

import { createLogger } from 'helpers/logger';

const AnimationPlayState = {
    PAUSED: 'paused',
    RUNNING: 'running',
};
export default class VideoHeroController {
    _isInitialized = false;
    _currentVideo = null;
    _heroVolume = null;
    _heroVolumeBackground = null;
    _hero = null;
    _self = null;
    _withInteraction = false;
    _isWorking = false;
    _isDesktop = false;
    _standAlone = false;
    _videoContainer = null;
    _trackingPlayingCallback = null;
    _trackingErrorCallback = null;
    _trackingEndedCallback = null;
    _trackingVolumeCallback = null;
    _staticSlideDuration = 10; // duration of slides without video in seconds
    _videoEndedOnShowPage = false;
    _noVideoOnThisSlide = false;
    _eventHandlers = [];
    _volumeEnabled = CBS.Registry.videoPreview.volumeEnabled;
    _videoIdPrefix = 'marqueeVideo';

    constructor(heroVolume, hero, delayStart = 3000) {
        this._heroVolume = heroVolume;
        this._delayStart = delayStart;

        const hpAbTestDelayActiveVariant =
            CBS?.AbTestsConfigs?.modules?.HomePageMinimizePreviewDelay?.activeVariant;
        switch (hpAbTestDelayActiveVariant) {
            case '0_sec':
                this._delayStart = 0;
                break;
            case '0_5_sec':
                this._delayStart = 500;
                break;
            case '1_sec':
                this._delayStart = 1000;
                break;
            case '2_sec':
                this._delayStart = 2000;
                break;
            case 'control':
                this._delayStart = 3000;
                break;
        }

        if (!document.getElementsByClassName('hero-volume-background').length) {
            this._noVideoOnThisSlide = true;
        } else {
            this._heroVolumeBackground = document.getElementsByClassName('hero-volume-background');
        }

        this._hero = hero;
        if (this._hero === null) {
            this._standAlone = true;
        }
        this._self = this;
        this._initAutoplay = true;
        this.isMarquee =
            document.getElementsByClassName('swiper-pagination-bullets').length > 0 ? true : false;

        this._logger = createLogger('VideoHeroController');

        if (window.innerWidth > 1023) {
            if (this.isMarquee) {
                this.stopHero(true);
            }
            this._isDesktop = true;
        }
        this.addVolumeEvents();
        this.addWindowResizeEvent();
        this.addWindowScrollEvent();
        this.setCarouselVideoEvents();
        this.setupHeroVolumeObserver();
    }

    async playSplice(index) {
        const container = this.getVideoContainer(index);

        if (this._isWorking) {
            return;
        }

        if (!this._isDesktop || !container || !container.dataset?.mediaUrl) {
            this.stopHero(false);
            return;
        }

        // Fix for when playSplice is fired from multiple events
        if (this._currentVideo?.id === container.id) {
            return;
        }

        this._videoContainer = container;
        this._isWorking = true;

        // Stop hero until duration is set
        // Video end will trigger next slide
        this.stopHero(true);

        // Prevent unmuted autoplay errors
        await checkAutoplayCapabilities();

        try {
            await this.createVideo(container.id, container.dataset.mediaUrl, container.dataset.hls);
        } catch (e) {
            this.onErrorHandler(e);
            this._logger.log(`Error creating player: ${container.dataset.mediaUrl}`);
        } finally {
            this._isWorking = false;
        }
    }

    setBulletAnimation(state) {
        this._logger.log(`Set bullet animation to  ${state}`);
        const activeBullet = document.querySelector('.swiper-pagination-bullet-active b');
        activeBullet.style.animationPlayState = state;
    }

    pauseSplice() {
        if (!this._standAlone) {
            this.setBulletAnimation(AnimationPlayState.PAUSED);
            this.autoplay(false);
        }
        try {
            this._currentVideo.suspendPlayback();
        } catch (e) {
            this._logger.log(e);
        }
    }

    resumeSplice() {
        const index = this._standAlone ? 0 : this._hero.realIndex;
        const video = document.getElementById(this._videoIdPrefix + index);
        const isMuted = this.isMuted();

        if (!this._standAlone) {
            this.setBulletAnimation(AnimationPlayState.RUNNING);
        }

        if (video && this._currentVideo) {
            this.setMute(isMuted);
            this._currentVideo.resumePlayback();
        } else {
            // Restart autoplay for images
            this.autoplay(true);
        }
    }

    async playVideo() {
        try {
            if (this._currentVideo.isPlaying()) {
                return;
            }
            await this._currentVideo.play();

            if (this._trackingPlayingCallback) {
                this._trackingPlayingCallback();
            }
        } catch (e) {
            this._logger.log("Couldn't play video");
            this.onErrorHandler(e);
        }
    }

    autoplay(autoplay = true) {
        const swiper = document.getElementById('hero-content-slider').swiper;

        if (this._standAlone) {
            return;
        }

        if (autoplay) {
            this._logger.log('Start swiper autoplay');
            return swiper.autoplay.start();
        }

        this._logger.log('Stop swiper autoplay');
        swiper.autoplay.stop();
    }

    async createVideo(id, mediaUrl, isHLS) {
        this._currentVideo = new VideoPreview({
            id: id,
            selector: `#${id}`,
            mediaUrl: mediaUrl,
            delayStart: this._delayStart,
            destroyAfterPlay: false,
            isHls: isHLS,
        });

        this.addEvents();

        await this._currentVideo.setPlayer();
    }

    async destroyVideo() {
        if (!this._currentVideo) return;

        this._currentVideo.removePlayer();
        this._currentVideo = null;
        this._videoContainer = null;
    }

    addWindowScrollEvent() {
        if (
            window.CBS.Registry.pageType !== 'HOMEPAGE' &&
            window.CBS.Registry.pageType !== 'HOME'
        ) {
            window.addEventListener(
                'scroll',
                this.debounce(() => {
                    if (window.innerWidth > 1023 && this._currentVideo) {
                        const isHeroVisible = $('.hero__grid').isOnScreen();
                        if (!isHeroVisible) {
                            try {
                                this.pauseSplice();
                            } catch (e) {}
                        } else {
                            //prevent video from replaying on show page
                            if (this._videoEndedOnShowPage === false) {
                                this.playVideo();
                            }
                        }
                    }
                }),
            );
        }
    }

    addWindowResizeEvent() {
        window.addEventListener(
            'resize',
            this.debounce((e) => {
                this._logger.log('Resize');
                if (e.target.innerWidth > 1023) {
                    this._isDesktop = true;

                    if (!this._isWorking && !this._currentVideo) {
                        if (this.isHeroVisible()) {
                            // Larger screen, attempt to create video
                            this.playSplice();
                        }
                    }
                } else {
                    this._isDesktop = false;

                    if (this._isWorking || this._currentVideo) {
                        this._isWorking = false;
                        this.reset();

                        if (this._trackingEndedCallback) {
                            this._trackingEndedCallback();
                        }
                    }
                }
            }),
        );
    }

    addVolumeEvents() {
        this._heroVolume.show();
        this._heroVolume.on('click', (e) => {
            e.preventDefault();
            this.setMute(!this._heroVolume.hasClass('muted'), true);
        });
    }
    setupHeroVolumeObserver() {
        const heroVolume = document.querySelector('.hero-volume');
        const brandLogo = document.querySelector('.brand-logo');

        if (heroVolume) {
            const observer = new MutationObserver((mutationsList) => {
                mutationsList.forEach((mutation) => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                        const isHeroVolumeVisible =
                            !heroVolume.classList['value'].includes('disabled');

                        if (brandLogo) {
                            if (!isHeroVolumeVisible) {
                                brandLogo.style.position = 'relative';
                                brandLogo.style.bottom = '0px';
                                brandLogo.style.right = '0px';
                            } else {
                                brandLogo.style.position = 'absolute';
                                brandLogo.style.bottom = '80px';
                                brandLogo.style.right = '0px';
                            }
                        }
                    }
                });
            });
            observer.observe(heroVolume, { attributes: true });
        }
    }

    debounce(func, timeout = 250) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                func.apply(this, args);
            }, timeout);
        };
    }

    stopHero(sw, withInteraction = false) {
        this._withInteraction = withInteraction;
        if (!this._standAlone) {
            if (sw) {
                this.pauseSplice();
            } else {
                this.resumeSplice();
            }
        }
    }

    onContentDurationAvailable() {
        const index = this.getIndexFromId(this._currentVideo.id);

        if (this._standAlone) {
            return;
        }

        // Update the slide duration to the length of the video + 3 secs for poster display before video start
        let duration = Math.round(this._currentVideo.getContentDuration()) + 3;

        // Wait an extra second for first slide
        if (index === 0) {
            duration++;
        }

        this.setSlideAnimationDuration(index, duration, true);
        // Give appearance of autoplay, navigation is at end of video
        // Using actual autoplay causes unexpected navigation and timing issues
        this.setBulletAnimation(AnimationPlayState.RUNNING);
    }

    setSlideAnimationDuration(index, duration, restart = false) {
        if (this._standAlone) return;

        const bullet = this._hero.pagination.bullets[index];
        const el = bullet.querySelector('b');
        const videoSlide = document.querySelector(
            '.hero__content-wrapper .swiper-wrapper li:nth-child(' + (index + 1) + ')',
        );

        // Set the progress bar to the duration
        el.style.animationDuration = `${duration}s`;
        videoSlide.dataset.swiperAutoplay = duration * 1000;

        this._logger.log(`Set duration of ${duration}s`);

        if (restart) {
            this.cloneAndReplace(el);
        }
    }

    // Used to reset animation
    cloneAndReplace(el) {
        const newEl = el.cloneNode(true);
        el.parentNode.replaceChild(newEl, el);
    }

    getIndexFromId(id) {
        return parseInt(id.replace(this._videoIdPrefix, ''));
    }

    getVideoId(index) {
        return this._videoIdPrefix + index;
    }

    getVideoContainer(index) {
        const id = this.getVideoId(index ?? this._hero?.realIndex ?? 0);

        return document.getElementById(id);
    }

    getPlayerEvents() {
        return [
            {
                name: VideoPreviewEvent.CONTENT_DURATION_AVAILABLE,
                handler: this.onContentDurationAvailable.bind(this),
            },
            {
                name: VideoPreviewEvent.CONTENT_START,
                handler: this.onReadyHandler.bind(this),
            },
            {
                name: VideoPreviewEvent.ENDED,
                handler: this.onEndedHandler.bind(this),
            },
            {
                name: VideoPreviewEvent.ERROR,
                handler: this.onErrorHandler.bind(this),
            },
        ];
    }

    addEvents() {
        this.getPlayerEvents().forEach((event) => {
            this._currentVideo.addEventListener(event.name, event.handler);
        });
    }

    setCarouselVideoEvents() {
        document.body.addEventListener('carouselVideoStart', () => {
            this.setMute(this.isMuted(), false, false);
            if ($('.hero__grid').isOnScreen()) {
                this.pauseSplice();
                if (this._trackingEndedCallback) {
                    this._trackingEndedCallback();
                }
            }
        });

        document.body.addEventListener('carouselVideoStop', () => {
            this.setMute(this.isMuted(), false, false);
            if ($('.hero__grid').isOnScreen()) {
                this.resumeSplice();
            }
        });
    }

    onErrorHandler(e) {
        this._logger.log(e);
        this.reset(true);

        if (this._trackingErrorCallback) {
            this._trackingErrorCallback();
        }
    }

    onEndedHandler(e) {
        this._logger.log('Video ended');

        if (this._trackingEndedCallback) {
            // Pass duration from event handler, as current video
            // is set to zero
            this._trackingEndedCallback(e.detail?.contentTime);
        }

        if (this.isMarquee) {
            // This should only happen when playback is completed,
            // or when duration is not zero (playback error)
            // Otherwise we will get unexpected navigation
            if (!e.detail.interrupted && e.detail.contentDuration !== 0) {
                this._hero.slideNext();
            }

            if (this._hero.isEnd) {
                this.hideVideo();
            }
        } else {
            this._videoEndedOnShowPage = true;
            this.hideVideo();
        }
    }

    onReadyHandler() {
        this._logger.log('Content started');
        this.showVideo();
        this.enableVolume();

        if (this._trackingPlayingCallback) {
            this._trackingPlayingCallback();
        }
    }

    isHeroVisible() {
        const container = document.getElementById('hero-content');

        if (!container) return false;

        return container.checkVisibility();
    }

    showVideo() {
        const isMuted = this.isMuted();

        $('.marqueeVideoContainer').removeClass('disabled').fadeIn(1200);
        $('#hero-slider .swiper-slide-active picture').hide();

        if (isMuted) {
            this.setMute();
        } else {
            this.setMute(false, false, false);
        }

        this._logger.log('Show Video');
    }

    hideVideo() {
        $('.marqueeVideoContainer').fadeOut(400).addClass('disabled');
        $('#hero-slider .swiper-slide-active picture').show();
        this.disableVolume();
    }

    isMuted() {
        return !this._volumeEnabled || localStorage.getItem(LocalStorageName.VOLUME_ON) !== '1';
    }

    isPlaying() {
        if (!this._currentVideo) {
            return false;
        }
        return this._currentVideo.isPlaying();
    }

    disableVolume() {
        this._heroVolume.addClass('disabled');

        if (this._noVideoOnThisSlide && this._heroVolumeBackground) {
            this._heroVolumeBackground[0].classList.add('disabled');
        }
    }

    enableVolume() {
        if (!this._volumeEnabled) return;

        this._heroVolume.removeClass('disabled');

        if (!this._noVideoOnThisSlide && this._heroVolumeBackground) {
            this._heroVolumeBackground[0].classList.remove('disable');
        }
    }

    setMute(mutePlayer = true, callTracking = false, saveSetting = true) {
        let trackAction = null;
        if (this._currentVideo) {
            this._currentVideo.mute(mutePlayer);
        }
        if (mutePlayer) {
            this._heroVolume.addClass('muted').attr('title', 'Unmute Audio');
            if (saveSetting) {
                localStorage.setItem(LocalStorageName.VOLUME_ON, 0);
            }
            trackAction = 'trackMutePlayer';
        } else {
            this._heroVolume.removeClass('muted').attr('title', 'Mute Audio');
            if (saveSetting) {
                localStorage.setItem(LocalStorageName.VOLUME_ON, 1);
            }
            trackAction = 'trackUnmutePlayer';
        }
        if (callTracking) {
            if (this._trackingVolumeCallback) {
                this._trackingVolumeCallback(trackAction);
            }
        }
    }

    async reset(autoplay = true) {
        this._logger.log('Reset');
        this.hideVideo();
        this.resetBulletAnimation();

        this._isWorking = false;

        await this.destroyVideo();

        if (autoplay) this.autoplay();
    }

    resetBulletAnimation() {
        if (this._standAlone) return;

        this._logger.log(`Reset bullet animation`);
        this.setSlideAnimationDuration(this._hero.realIndex, this._staticSlideDuration, true);
        this.setBulletAnimation(AnimationPlayState.RUNNING);
    }

    async restart() {
        if (!this._currentVideo) return;

        try {
            if (this.isPlaying(this._currentVideo)) {
                // Restart animation
                this.onContentDurationAvailable();

                // Restart player
                await this._currentVideo?.seek(0);

                if (this._trackingPlayingCallback) {
                    this._trackingPlayingCallback();
                }
            }
        } catch (e) {
            this._logger.log(e);
        }
    }

    set trackingPlayingCallback(val) {
        this._trackingPlayingCallback = val;
    }

    set trackingErrorCallback(val) {
        this._trackingErrorCallback = val;
    }

    set trackingEndedCallback(val) {
        this._trackingEndedCallback = val;
    }

    set trackingVolumeCallback(val) {
        this._trackingVolumeCallback = val;
    }
}
